"use client";

import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { garagemAuth } from "@/db/auth/client";
import { toast } from "@garagem/ui/components/sonner";
import { authQueryKeys } from "./query-keys";
import {
  builtInRoles,
  roleNames,
  roleDescriptions,
  rolePermissions,
} from "@/db/auth/permissions";

type CustomRoleLike = {
  id: string;
  name: string;
  description?: string;
  isBuiltIn?: boolean;
  permissions?: Record<string, unknown>;
};

type CustomRolesListResponse = {
  data?: { roles?: CustomRoleLike[] };
  error?: unknown;
};

type CustomRoleMutationResponse = {
  data?: { role?: CustomRoleLike };
  role?: CustomRoleLike;
  error?: unknown;
};

// Custom roles live on the tenant, but listCustomRoles is session-scoped
// (active Better Auth org). Callers pass `useTenant().activeTenantId` — the
// org id — so sibling orgs on a shared tenant do not share this cache.
export function useCustomRoles(tenantId?: string) {
  return useQuery({
    queryKey: authQueryKeys.organization.customRoles(tenantId || ""),
    queryFn: async () => {
      if (!garagemAuth?.customRoles?.listCustomRoles) {
        console.warn("Custom roles API not available");
        return [];
      }

      try {
        const response =
          (await garagemAuth.customRoles.listCustomRoles()) as CustomRolesListResponse;
        return response.data?.roles || [];
      } catch (error) {
        console.error("Failed to fetch custom roles:", error);
        return [];
      }
    },
    enabled: !!tenantId,
  });
}

export function useCreateCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description?: string;
      permissions: Record<
        string,
        string[] | { company?: string[]; team?: string[]; user?: string[] }
      >;
    }) => {
      if (!garagemAuth?.customRoles?.createCustomRole) {
        throw new Error("Custom roles API not available");
      }

      const response = (await garagemAuth.customRoles.createCustomRole(
        data
      )) as CustomRoleMutationResponse;
      if (response.error) throw response.error;
      return response.data?.role || response.role;
    },
    onSuccess: () => {
      toast.success("Papel personalizado criado com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
    onError: (error) => {
      console.error("Error creating custom role:", error);
      toast.error("Erro ao criar papel personalizado", {
        description: error?.message || "Ocorreu um erro ao criar o papel.",
      });
    },
  });
}

export function useUpdateCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        description?: string;
        permissions?: Record<
          string,
          string[] | { company?: string[]; team?: string[]; user?: string[] }
        >;
      };
    }) => {
      if (!garagemAuth?.customRoles?.updateCustomRole) {
        throw new Error("Custom roles API not available");
      }

      const response = (await garagemAuth.customRoles.updateCustomRole({
        id,
        data,
      })) as CustomRoleMutationResponse;
      if (response.error) throw response.error;
      return response.data?.role;
    },
    onSuccess: () => {
      toast.success("Papel atualizado com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
    onError: (error) => {
      toast.error("Erro ao atualizar papel", {
        description: error?.message || "Ocorreu um erro ao atualizar o papel.",
      });
    },
  });
}

export function useDeleteCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      if (!garagemAuth?.customRoles?.deleteCustomRole) {
        throw new Error("Custom roles API not available");
      }

      const response = await garagemAuth.customRoles.deleteCustomRole({ id });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Papel excluído com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
    onError: (error) => {
      toast.error("Erro ao excluir papel", {
        description: error?.message || "Ocorreu um erro ao excluir o papel.",
      });
    },
  });
}

export function useAssignCustomRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      roleId,
    }: {
      memberId: string;
      roleId: string;
    }) => {
      if (!garagemAuth?.customRoles?.assignCustomRole) {
        throw new Error("Custom roles API not available");
      }

      const response = await garagemAuth.customRoles.assignCustomRole({
        memberId,
        roleId,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Papel atribuído com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
    onError: (error) => {
      toast.error("Erro ao atribuir papel", {
        description: error?.message || "Ocorreu um erro ao atribuir o papel.",
      });
    },
  });
}

export function useAssignBuiltInRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      memberId,
      role,
    }: {
      memberId: string;
      role: string;
    }) => {
      if (!garagemAuth?.customRoles?.assignBuiltInRole) {
        throw new Error("Built-in roles API not available");
      }

      const response = await garagemAuth.customRoles.assignBuiltInRole({
        memberId,
        role,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Papel atribuído com sucesso!");
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.organization.all,
      });
    },
    onError: (error) => {
      toast.error("Erro ao atribuir papel", {
        description: error?.message || "Ocorreu um erro ao atribuir o papel.",
      });
    },
  });
}

// Built-in roles hook
export function useBuiltInRoles() {
  return useQuery({
    queryKey: authQueryKeys.organization.roles(),
    queryFn: async () =>
      // Transform the built-in roles from permissions.ts into the format expected by components
      // Filter out the 'system' role as it's for internal admin use only
      Object.entries(builtInRoles)
        .filter(([key]) => key !== "system") // Exclude system role from UI
        .filter(([key]) => !["owner", "admin", "member"].includes(key)) // Legacy English org keys mirror PT roles — do not duplicate in selectors
        .map(([key, _role]) => ({
          id: key,
          name: roleNames[key as keyof typeof roleNames],
          description: roleDescriptions[key as keyof typeof roleDescriptions],
          isBuiltIn: true,
          // permission-check-ok: role-editor data projection; no access decision.
          permissions: rolePermissions[key as keyof typeof rolePermissions],
        })),
    staleTime: Infinity, // Built-in roles don't change
  });
}

const NO_ROLES: never[] = [];

// Combined roles hook - returns both built-in and custom roles.
// `data` is referentially stable between renders while the queries are
// unchanged: callers put it in memo/effect deps (e.g. `visibleRoles` →
// `visibleRoleIds` → task filter builder), and a fresh array every render
// used to re-run that whole chain on every page render.
export function useAllRoles(tenantId?: string) {
  const { data: builtInRoles = NO_ROLES } = useBuiltInRoles();
  const { data: customRoles = NO_ROLES } = useCustomRoles(tenantId);
  const data = useMemo(
    () => [...builtInRoles, ...customRoles],
    [builtInRoles, customRoles]
  );

  return { data, builtInRoles, customRoles };
}
